import { RefreshCw, X } from 'lucide-react';

const THEME_SETTING_KEYS = [
    'screenBg',
    'screenText',
    'primaryBg',
    'primaryText',
    'highlightBg',
    'highlightText',
    'warningBg',
    'warningText',
    'infoBg',
    'infoText',
    'border',
    'baseTextSize',
    'fontFamily',
    'heroTitle',
    'heroSubtitle'
];

const SettingsPanel = ({
    currentDomainName,
    isSavingTheme,
    setIsSavingTheme,
    onClose,
    draftThemeSettings,
    setDraftThemeSettings,
    setThemeSettings,
    fontOptions,
    defaultThemeSettings,
    enforceWordLimit,
    clinicData,
    setClinicData,
    clinicKey,
    sendHttpRequest,
    unwrapClinicAttributeValue,
    setError
}) => {
    const getThemeSettingsToSave = () => {
        const themeSettingsToSave = Object.fromEntries(
            THEME_SETTING_KEYS.map((key) => [key, draftThemeSettings[key]])
        );

        return {
            ...themeSettingsToSave,
            heroTitle: enforceWordLimit(String(draftThemeSettings.heroTitle || ''), 20).trim(),
            heroSubtitle: enforceWordLimit(String(draftThemeSettings.heroSubtitle || ''), 30).trim(),
            domainName: currentDomainName
        };
    };

    const saveThemeSettings = async () => {
        const existingBranding = unwrapClinicAttributeValue(clinicData?.branding);
        const existingDomainSettings = existingBranding?.domainSettings && typeof existingBranding.domainSettings === 'object'
            ? existingBranding.domainSettings
            : {};
        const existingCurrentDomainSettings = existingDomainSettings[currentDomainName] && typeof existingDomainSettings[currentDomainName] === 'object'
            ? existingDomainSettings[currentDomainName]
            : {};
        const settingsToSave = {
            ...existingCurrentDomainSettings,
            ...getThemeSettingsToSave()
        };
        const scopedBrandingToSave = {
            domainSettings: {
                ...existingDomainSettings,
                [currentDomainName]: settingsToSave
            }
        };

        setIsSavingTheme(true);
        try {
            await sendHttpRequest(`/clinic/${encodeURIComponent(clinicKey)}`, {
                method: 'PUT',
                headers: {
                    'x-api-key': clinicKey
                },
                body: JSON.stringify({
                    clinicId: clinicKey,
                    clinic: {
                        ...clinicData,
                        branding: scopedBrandingToSave
                    }
                })
            }, 30000);
            setThemeSettings(settingsToSave);
            setDraftThemeSettings(settingsToSave);
            setClinicData(prev => ({
                ...(prev || {}),
                branding: scopedBrandingToSave
            }));
            onClose();
        } catch (error) {
            console.error("Theme save failed", error);
            setError(error.message || "Unable to save theme settings. Please try again.");
        } finally {
            setIsSavingTheme(false);
        }
    };

    const resetDraftThemeSettings = () => {
        setDraftThemeSettings(prev => ({
            ...prev,
            ...Object.fromEntries(THEME_SETTING_KEYS.map((key) => [key, defaultThemeSettings[key]]))
        }));
    };

    return (
        <div className="absolute top-20 right-2 sm:right-4 z-[190] w-[min(96vw,480px)]">
            <div className="absolute -top-3 right-6 w-6 h-6 rotate-45 bg-primary border-l border-t border-border shadow-sm"></div>
            <div className="relative bg-primary text-primary rounded-[1.5rem] sm:rounded-[1.75rem] border border-border shadow-2xl p-3 sm:p-5 flex flex-col gap-3 sm:gap-4 max-h-[calc(100vh-7rem)] overflow-hidden">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-sm font-black text-primary">Settings V2.0</p>
                        <p className="text-[11px] text-primary mt-1">Live preview updates while you edit.</p>
                        <p className="mt-2 inline-flex max-w-full rounded-full border border-border bg-primary-60 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-primary">
                            Domain: <span className="ml-1 truncate text-primary">{currentDomainName}</span>
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => !isSavingTheme && onClose()}
                        disabled={isSavingTheme}
                        className="shrink-0 text-primary p-2 rounded-xl border border-border bg-primary disabled:opacity-50"
                        aria-label="Close settings"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-4 pb-1">
                    <label className="block space-y-2">
                        <span className="text-[10px] font-black uppercase tracking-widest text-primary">Font Family</span>
                        <select
                            value={draftThemeSettings.fontFamily}
                            onChange={(e) => setDraftThemeSettings(prev => ({ ...prev, fontFamily: e.target.value }))}
                            className="w-full p-2.5 rounded-xl bg-primary border border-border text-xs font-bold text-primary outline-none focus:ring-2 focus:ring-border-100"
                        >
                            {fontOptions.map(font => (
                                <option key={font.label} value={font.value}>{font.label}</option>
                            ))}
                        </select>
                    </label>

                    <label className="block space-y-2">
                        <div className="flex items-center justify-between gap-3">
                            <span className="text-[10px] font-black uppercase tracking-widest text-primary">Base Text</span>
                            <span className="text-[10px] font-black text-primary">{draftThemeSettings.baseTextSize}px</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="range"
                                min="12"
                                max="22"
                                step="1"
                                value={draftThemeSettings.baseTextSize}
                                onChange={(e) => setDraftThemeSettings(prev => ({ ...prev, baseTextSize: Number(e.target.value) }))}
                                className="flex-1 accent-[var(--falcon-highlight)]"
                            />
                            <input
                                type="number"
                                min="12"
                                max="22"
                                step="1"
                                value={draftThemeSettings.baseTextSize}
                                onChange={(e) => setDraftThemeSettings(prev => ({ ...prev, baseTextSize: Number(e.target.value) || defaultThemeSettings.baseTextSize }))}
                                className="w-16 p-2 rounded-xl bg-primary border border-border text-[11px] font-bold text-primary outline-none focus:ring-2 focus:ring-border-100"
                            />
                        </div>
                    </label>

                    <label className="block space-y-2">
                        <span className="text-[10px] font-black uppercase tracking-widest text-primary">Hero title</span>
                        <input
                            type="text"
                            value={draftThemeSettings.heroTitle}
                            onChange={(e) => setDraftThemeSettings(prev => ({ ...prev, heroTitle: enforceWordLimit(e.target.value, 20) }))}
                            className="w-full p-3 rounded-2xl bg-primary border border-border text-[12px] font-bold text-primary outline-none focus:ring-2 focus:ring-border-100 transition-all"
                        />
                        <p className="text-[10px] text-primary/80">Max 20 words.</p>
                    </label>

                    <label className="block space-y-2">
                        <span className="text-[10px] font-black uppercase tracking-widest text-primary">Hero subtitle</span>
                        <textarea
                            rows={3}
                            value={draftThemeSettings.heroSubtitle}
                            onChange={(e) => setDraftThemeSettings(prev => ({ ...prev, heroSubtitle: enforceWordLimit(e.target.value, 30) }))}
                            className="w-full p-3 rounded-2xl bg-primary border border-border text-[12px] font-bold text-primary outline-none focus:ring-2 focus:ring-border-100 transition-all"
                        />
                        <p className="text-[10px] text-primary/80">Max 30 words.</p>
                    </label>

                    <h3 className="text-[10px] font-black uppercase tracking-widest text-primary">Theme Settings</h3>

                    {[
                        { label: 'Screen', bgKey: 'screenBg', textKey: 'screenText' },
                        { label: 'Background', bgKey: 'primaryBg', textKey: 'primaryText' },
                        { label: 'Warning', bgKey: 'warningBg', textKey: 'warningText' },
                        { label: 'Info', bgKey: 'infoBg', textKey: 'infoText' },
                        { label: 'Highlight', bgKey: 'highlightBg', textKey: 'highlightText' }
                    ].map((group) => (
                        <div key={group.label} className="rounded-2xl border border-border p-3 space-y-3">
                            <div className="text-[10px] font-black uppercase tracking-widest text-primary">{group.label}</div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-primary">Background</span>
                                    <input
                                        type="color"
                                        value={draftThemeSettings[group.bgKey]}
                                        onChange={(e) => setDraftThemeSettings(prev => ({ ...prev, [group.bgKey]: e.target.value }))}
                                        className="w-full h-9 rounded-xl border border-border bg-primary cursor-pointer"
                                    />
                                    <input
                                        type="text"
                                        value={draftThemeSettings[group.bgKey]}
                                        onChange={(e) => setDraftThemeSettings(prev => ({ ...prev, [group.bgKey]: e.target.value }))}
                                        className="w-full p-2 rounded-xl bg-primary border border-border text-[10px] font-bold text-primary uppercase outline-none focus:ring-2 focus:ring-border-100"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-primary">Foreground</span>
                                    <input
                                        type="color"
                                        value={draftThemeSettings[group.textKey]}
                                        onChange={(e) => setDraftThemeSettings(prev => ({ ...prev, [group.textKey]: e.target.value }))}
                                        className="w-full h-9 rounded-xl border border-border bg-primary cursor-pointer"
                                    />
                                    <input
                                        type="text"
                                        value={draftThemeSettings[group.textKey]}
                                        onChange={(e) => setDraftThemeSettings(prev => ({ ...prev, [group.textKey]: e.target.value }))}
                                        className="w-full p-2 rounded-xl bg-primary border border-border text-[10px] font-bold text-primary uppercase outline-none focus:ring-2 focus:ring-border-100"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}

                    <label className="block space-y-2">
                        <span className="text-[10px] font-black uppercase tracking-widest text-primary">Border</span>
                        <div className="flex items-center gap-2">
                            <input
                                type="color"
                                value={draftThemeSettings.border}
                                onChange={(e) => setDraftThemeSettings(prev => ({ ...prev, border: e.target.value }))}
                                className="w-12 h-9 rounded-xl border border-border bg-primary cursor-pointer"
                            />
                            <input
                                type="text"
                                value={draftThemeSettings.border}
                                onChange={(e) => setDraftThemeSettings(prev => ({ ...prev, border: e.target.value }))}
                                className="flex-1 p-2 rounded-xl bg-primary border border-border text-[10px] font-bold text-primary uppercase outline-none focus:ring-2 focus:ring-border-100"
                            />
                        </div>
                    </label>
                </div>

                <div className="grid grid-cols-3 gap-2 pt-1">
                    <button type="button" onClick={saveThemeSettings} disabled={isSavingTheme} className="py-2.5 rounded-xl bg-highlight text-highlight text-[11px] font-black transition-all disabled:opacity-50 flex items-center justify-center gap-1.5">
                        {isSavingTheme && <RefreshCw size={12} className="animate-spin" />}
                        {isSavingTheme ? 'Saving' : 'Save'}
                    </button>
                    <button type="button" onClick={resetDraftThemeSettings} disabled={isSavingTheme} className="py-2.5 rounded-xl border border-border text-primary text-[11px] font-black hover:bg-highlight transition-all disabled:opacity-50">
                        Reset
                    </button>
                    <button type="button" onClick={() => onClose()} disabled={isSavingTheme} className="py-2.5 rounded-xl border border-border text-primary text-[11px] font-black hover:bg-highlight transition-all disabled:opacity-50">
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SettingsPanel;
